const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "m.youtube.com",
  "youtube-nocookie.com",
  "youtu.be",
]);

/** Normalize a YouTube watch/share/embed URL to a privacy-enhanced embed URL. */
export function toYouTubeEmbedUrl(
  input: string | null | undefined,
): string | null {
  if (!input?.trim()) return null;

  try {
    const url = new URL(input.trim());
    const host = url.hostname.replace(/^www\./, "");
    if (!YOUTUBE_HOSTS.has(host)) return null;

    const id = youtubeVideoId(url, host);
    if (!id) return null;

    const embed = new URL(`https://www.youtube-nocookie.com/embed/${id}`);
    const start = startSeconds(url);
    if (start) embed.searchParams.set("start", String(start));
    return embed.toString();
  } catch {
    return null;
  }
}

function youtubeVideoId(url: URL, host: string): string | null {
  if (host === "youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0];
    return isVideoId(id) ? id : null;
  }

  const path = url.pathname;
  if (
    path.startsWith("/embed/") ||
    path.startsWith("/shorts/") ||
    path.startsWith("/live/")
  ) {
    const id = path.split("/")[2];
    return isVideoId(id) ? id : null;
  }

  const fromQuery = url.searchParams.get("v");
  return isVideoId(fromQuery) ? fromQuery : null;
}

function startSeconds(url: URL): number | null {
  const start = url.searchParams.get("start") ?? url.searchParams.get("t");
  if (!start) return null;

  if (/^\d+$/.test(start)) return Number(start);

  const match = start.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!match) return null;

  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2] ?? 0);
  const seconds = Number(match[3] ?? 0);
  const total = hours * 3600 + minutes * 60 + seconds;
  return total > 0 ? total : null;
}

function isVideoId(value: string | null | undefined): value is string {
  return Boolean(value && /^[\w-]{11}$/.test(value));
}

export const WIDTH_CLASS = {
  full: "w-full max-w-none",
  xl: "w-full max-w-5xl",
  lg: "w-full max-w-3xl",
  md: "w-full max-w-xl",
  sm: "w-full max-w-md",
} as const;

export type EmbedWidth = keyof typeof WIDTH_CLASS;

export const PADDING_CLASS = {
  base: {
    none: "p-0",
    sm: "p-4",
    md: "p-6",
    lg: "p-10",
  },
  tablet: {
    none: "md:p-0",
    sm: "md:p-4",
    md: "md:p-6",
    lg: "md:p-10",
  },
  desktop: {
    none: "lg:p-0",
    sm: "lg:p-4",
    md: "lg:p-6",
    lg: "lg:p-10",
  },
} as const;

export type EmbedPadding = keyof typeof PADDING_CLASS.base;

export function widthClass(value: string | null | undefined): string {
  if (value && value in WIDTH_CLASS) {
    return WIDTH_CLASS[value as EmbedWidth];
  }
  return WIDTH_CLASS.lg;
}

export function paddingClass(
  padding: string | null | undefined,
  tabletPadding: string | null | undefined,
  mobilePadding: string | null | undefined,
): string {
  const fallback: EmbedPadding =
    padding && padding in PADDING_CLASS.base
      ? (padding as EmbedPadding)
      : "none";
  const mobile: EmbedPadding =
    mobilePadding && mobilePadding in PADDING_CLASS.base
      ? (mobilePadding as EmbedPadding)
      : fallback;
  const tablet: EmbedPadding =
    tabletPadding && tabletPadding in PADDING_CLASS.base
      ? (tabletPadding as EmbedPadding)
      : fallback;

  if (!tabletPadding && !mobilePadding) {
    return PADDING_CLASS.base[fallback];
  }

  return [
    PADDING_CLASS.base[mobile],
    PADDING_CLASS.tablet[tablet],
    PADDING_CLASS.desktop[fallback],
  ].join(" ");
}
